import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { useProperties } from '@/hooks/useProperties';
import { PROPERTY_TYPES, PROPERTY_PURPOSES } from '@/types/property';
import type { PropertyType, PropertyPurpose } from '@/types/property';

export default function PropertiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Get filters from URL
  const purpose = searchParams.get('purpose') as PropertyPurpose | null;
  const type = searchParams.get('type') as PropertyType | null;
  const city = searchParams.get('city') || '';
  const search = searchParams.get('search') || '';
  const bedrooms = searchParams.get('bedrooms') ? Number(searchParams.get('bedrooms')) : undefined;
  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) : 1;

  // Fetch properties with filters
  const { data, isLoading } = useProperties({
    filters: {
      purpose: purpose || undefined,
      type: type || undefined,
      city: city || undefined,
      search: search || undefined,
      bedrooms,
    },
    page: currentPage,
    pageSize: 12,
  });

  const properties = data?.properties || [];
  const totalPages = data?.totalPages || 1;
  const total = data?.total || 0;

  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const goToPage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    if (page > 1) {
      newParams.set('page', page.toString());
    } else {
      newParams.delete('page');
    }
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasFilters = purpose || type || city || search || bedrooms;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Purpose */}
      <div>
        <label className="mb-2 block text-sm font-medium">Finalidade</label>
        <Select
          value={purpose || 'all'}
          onValueChange={(val) => updateFilter('purpose', val === 'all' ? null : val)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.entries(PROPERTY_PURPOSES).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Type */}
      <div>
        <label className="mb-2 block text-sm font-medium">Tipo</label>
        <Select
          value={type || 'all'}
          onValueChange={(val) => updateFilter('type', val === 'all' ? null : val)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(PROPERTY_TYPES).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bedrooms */}
      <div>
        <label className="mb-2 block text-sm font-medium">Quartos</label>
        <Select
          value={bedrooms?.toString() || 'all'}
          onValueChange={(val) => updateFilter('bedrooms', val === 'all' ? null : val)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Qualquer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualquer</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* City */}
      <div>
        <label className="mb-2 block text-sm font-medium">Cidade</label>
        <Input
          placeholder="Digite a cidade"
          value={city}
          onChange={(e) => updateFilter('city', e.target.value || null)}
        />
      </div>

      {hasFilters && (
        <Button
          variant="outline"
          className="w-full"
          onClick={clearFilters}
        >
          <X className="mr-2 h-4 w-4" />
          Limpar Filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="container-custom py-8 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-border bg-card p-6">
              <h3 className="mb-6 font-bold text-foreground">Filtrar imóveis</h3>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div>
            {/* Mobile Search & Filter Bar */}
            <div className="mb-6 flex gap-3 lg:hidden">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar imóveis..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => updateFilter('search', e.target.value || null)}
                />
              </div>
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon">
                    <SlidersHorizontal className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Filtros</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Search */}
            <div className="mb-6 hidden lg:block">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por título, bairro..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => updateFilter('search', e.target.value || null)}
                />
              </div>
            </div>

            {/* Results count */}
            <div className="mb-6">
              <p className="text-sm text-muted-foreground">
                {isLoading ? 'Carregando...' : `${total} imóveis encontrados`}
              </p>
            </div>

            {/* Properties Grid */}
            {isLoading ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : properties.length > 0 ? (
              <>
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="h-10 w-10"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first, last, current, and pages around current
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, array) => {
                          // Add ellipsis where there are gaps
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          
                          return (
                            <div key={page} className="flex items-center gap-1">
                              {showEllipsis && (
                                <span className="px-2 text-muted-foreground">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="icon"
                                onClick={() => goToPage(page)}
                                className="h-10 w-10"
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="h-10 w-10"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-20 text-center">
                <p className="text-lg text-muted-foreground">
                  Nenhum imóvel encontrado com os filtros selecionados.
                </p>
                {hasFilters && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={clearFilters}
                  >
                    Limpar Filtros
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
